import { useState } from 'react';
import { errorMessage } from '@/api/client';
import { useReportPreview } from '@/api/hooks';
import { Dialog } from '@/components/Dialog';
import { Button, ErrorNote, Segmented, Skeleton } from '@/components/ui';

/**
 * What the next message will say. The HTML comes from the server, so it is shown in a fully
 * sandboxed iframe (no scripts, no same-origin, no forms, no top navigation).
 */
export function PreviewDialog({ reportId, onClose }: { reportId: string | null; onClose: () => void }) {
  const preview = useReportPreview(reportId ?? undefined);
  const [tab, setTab] = useState<'email' | 'text'>('email');

  return (
    <Dialog
      open={!!reportId}
      onClose={onClose}
      size="lg"
      title="Preview"
      description={preview.data ? preview.data.subject : 'What the next update will say.'}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <Segmented
        label="Preview format"
        size="sm"
        value={tab}
        onChange={setTab}
        options={[
          { value: 'email', label: 'Email' },
          { value: 'text', label: 'Text / chat' },
        ]}
        className="mb-4"
      />
      {preview.isPending && <Skeleton className="h-80" />}
      {preview.isError && <ErrorNote message={errorMessage(preview.error)} onRetry={() => void preview.refetch()} />}
      {preview.data &&
        (tab === 'email' ? (
          <iframe
            title="Email preview"
            sandbox=""
            referrerPolicy="no-referrer"
            srcDoc={preview.data.html}
            className="h-[420px] w-full rounded-control border border-border bg-white"
          />
        ) : (
          <pre className="max-h-[420px] overflow-auto rounded-control border border-border bg-muted p-4 font-sans text-sm whitespace-pre-wrap">
            {preview.data.text}
          </pre>
        ))}
    </Dialog>
  );
}
